/**
 * Compass Explorer — focus + zoom commands.
 *
 * These commands own the only legal write path into the Explorer's
 * focus / zoom / time model state. UI surfaces (focus picker, zoom
 * controls, follow-edge chips, URL routing, bookmarks) route through
 * these commands so navigation lives in the undo/redo history.
 *
 * Merge rules:
 *  - Rapid consecutive focus changes within MERGE_WINDOW_MS coalesce
 *    into a single undo step. Intent: "drill into VPC → then into
 *    subnet → then into one EC2" reads as one navigation gesture.
 *  - Zoom steps merge the same way within the same merge window.
 *
 * Non-goals:
 *  - These commands do NOT persist anything to the server. They only
 *    mutate the client-side `CompassViewPrefsStore`. Server state
 *    (graph snapshots, cached layouts) is read-only from here.
 *  - They do NOT touch nodes/edges. The Explorer computes its display
 *    from the raw graph + focus on every render; commands just change
 *    what the slice input is.
 */

import { BaseCommand, type ICommand } from '../../../core/commands/ICommand';
import {
  useCompassViewPrefs,
  type FocusSpec,
  type ZoomLevel,
  focusKey,
} from '../../../stores/compassViewPrefsStore';

/** Commands issued within this window merge into a single undo step. */
const MERGE_WINDOW_MS = 800;

/**
 * Change the Explorer's focus. Undo restores the previous focus, so
 * the standard undo shortcut doubles as a "back" gesture. Redo is
 * "forward."
 */
export class ChangeFocusCommand extends BaseCommand {
  readonly kind = 'compass:change-focus';
  private readonly prevFocus: FocusSpec;
  private readonly nextFocus: FocusSpec;

  constructor(nextFocus: FocusSpec, prevFocus?: FocusSpec) {
    super(
      'Change focus',
      `Focus → ${focusKey(nextFocus)}`,
    );
    this.nextFocus = nextFocus;
    // Capture the previous focus eagerly if not provided so execute()
    // is idempotent w.r.t. store state at construction time.
    this.prevFocus =
      prevFocus ?? useCompassViewPrefs.getState().focus;
  }

  execute(): void {
    useCompassViewPrefs.getState()._setFocus(this.nextFocus);
  }

  undo(): void {
    useCompassViewPrefs.getState()._setFocus(this.prevFocus);
  }

  redo(): void {
    this.execute();
  }

  canMergeWith(other: ICommand): boolean {
    if (!(other instanceof ChangeFocusCommand)) return false;
    // Only merge if both commands are from the same rapid sequence AND
    // they land on different focuses (no point merging a no-op with
    // anything). The second command wins; the first command's
    // prevFocus becomes the merged command's prevFocus.
    if (other.timestamp - this.timestamp > MERGE_WINDOW_MS) return false;
    return focusKey(this.nextFocus) !== focusKey(other.nextFocus);
  }

  mergeWith(other: ICommand): ICommand {
    if (!(other instanceof ChangeFocusCommand)) return this;
    const merged = new ChangeFocusCommand(other.nextFocus, this.prevFocus);
    merged.timestamp = other.timestamp;
    return merged;
  }

  override getMetadata(): Record<string, unknown> {
    return {
      kind: this.kind,
      prev: focusKey(this.prevFocus),
      next: focusKey(this.nextFocus),
    };
  }
}

/**
 * Change the Explorer's zoom level (1–5). Merges adjacent zoom steps
 * so `+ + +` from the keyboard coalesces into a single undo step.
 */
export class ChangeZoomLevelCommand extends BaseCommand {
  readonly kind = 'compass:change-zoom';
  private readonly prevZoom: ZoomLevel;
  private readonly nextZoom: ZoomLevel;

  constructor(nextZoom: ZoomLevel, prevZoom?: ZoomLevel) {
    super(
      'Change zoom level',
      `Zoom → L${nextZoom}`,
    );
    this.nextZoom = nextZoom;
    this.prevZoom =
      prevZoom ?? useCompassViewPrefs.getState().zoomLevel;
  }

  execute(): void {
    useCompassViewPrefs.getState()._setZoomLevel(this.nextZoom);
  }

  undo(): void {
    useCompassViewPrefs.getState()._setZoomLevel(this.prevZoom);
  }

  redo(): void {
    this.execute();
  }

  canMergeWith(other: ICommand): boolean {
    if (!(other instanceof ChangeZoomLevelCommand)) return false;
    if (other.timestamp - this.timestamp > MERGE_WINDOW_MS) return false;
    return this.nextZoom !== other.nextZoom;
  }

  mergeWith(other: ICommand): ICommand {
    if (!(other instanceof ChangeZoomLevelCommand)) return this;
    const merged = new ChangeZoomLevelCommand(other.nextZoom, this.prevZoom);
    merged.timestamp = other.timestamp;
    return merged;
  }

  override getMetadata(): Record<string, unknown> {
    return {
      kind: this.kind,
      prev: this.prevZoom,
      next: this.nextZoom,
    };
  }
}

/**
 * Scrub through historical graph versions (M6.C). Merges adjacent
 * scrubs the same way ChangeZoomLevelCommand does.
 */
export class ScrubTimeCommand extends BaseCommand {
  readonly kind = 'compass:scrub-time';
  private readonly prevVersion: number | null;
  private readonly nextVersion: number | null;

  constructor(nextVersion: number | null, prevVersion?: number | null) {
    super(
      'Scrub time',
      `Version → ${nextVersion ?? 'latest'}`,
    );
    this.nextVersion = nextVersion;
    this.prevVersion =
      prevVersion !== undefined
        ? prevVersion
        : useCompassViewPrefs.getState().graphVersion;
  }

  execute(): void {
    useCompassViewPrefs.getState()._setGraphVersion(this.nextVersion);
  }

  undo(): void {
    useCompassViewPrefs.getState()._setGraphVersion(this.prevVersion);
  }

  redo(): void {
    this.execute();
  }

  canMergeWith(other: ICommand): boolean {
    if (!(other instanceof ScrubTimeCommand)) return false;
    if (other.timestamp - this.timestamp > MERGE_WINDOW_MS) return false;
    return this.nextVersion !== other.nextVersion;
  }

  mergeWith(other: ICommand): ICommand {
    if (!(other instanceof ScrubTimeCommand)) return this;
    const merged = new ScrubTimeCommand(other.nextVersion, this.prevVersion);
    merged.timestamp = other.timestamp;
    return merged;
  }

  override getMetadata(): Record<string, unknown> {
    return {
      kind: this.kind,
      prev: this.prevVersion,
      next: this.nextVersion,
    };
  }
}

/**
 * Expand or collapse an infra-frame in place (Phase 3). The set of
 * currently-expanded frames lives on `compassViewPrefsStore.
 * expandedFrameIds` as an ordered array; toggling a frame id that is
 * already expanded removes it, otherwise appends it. Undo restores the
 * exact prior order so "expand VPC → expand subnet → undo" collapses
 * just the subnet.
 */
export class ToggleFrameExpandedCommand extends BaseCommand {
  readonly kind = 'compass:toggle-frame-expanded';
  private readonly frameId: string;
  private readonly prevIds: string[];
  private readonly nextIds: string[];

  constructor(frameId: string, prev?: string[]) {
    super(
      'Toggle frame expansion',
      `Frame → ${frameId}`,
    );
    this.frameId = frameId;
    const current = prev ?? useCompassViewPrefs.getState().expandedFrameIds;
    this.prevIds = current.slice();
    const idx = current.indexOf(frameId);
    this.nextIds =
      idx === -1
        ? [...current, frameId]
        : current.filter((id) => id !== frameId);
  }

  execute(): void {
    useCompassViewPrefs.getState()._setExpandedFrameIds(this.nextIds);
  }

  undo(): void {
    useCompassViewPrefs.getState()._setExpandedFrameIds(this.prevIds);
  }

  redo(): void {
    this.execute();
  }

  canMergeWith(_other: ICommand): boolean {
    // Don't merge — each expand/collapse should be its own undo step so
    // "drilled three levels → undo" only undoes the last step.
    return false;
  }

  mergeWith(_other: ICommand): ICommand {
    return this;
  }

  override getMetadata(): Record<string, unknown> {
    return {
      kind: this.kind,
      frameId: this.frameId,
      prevCount: this.prevIds.length,
      nextCount: this.nextIds.length,
    };
  }
}
